import apiClient from '../apiClient';

// Types for simplified clinic lab orders

export interface LabTestResult {
  id: string;
  testName: string;
  testCode?: string;
  result?: string;
  unit?: string;
  referenceRange?: string;
  status: 'pending' | 'normal' | 'abnormal' | 'critical';
  notes?: string;
}

export interface ClinicLabOrder {
  id: string;
  patientId: string;
  /** Encounter (note session) this order was raised in, when applicable. */
  medicalRecordId?: string | null;
  orderedBy: string;
  orderDate: string;
  status: 'ordered' | 'in-progress' | 'resulted' | 'cancelled';
  priority: 'routine' | 'urgent';
  tests: LabTestResult[];
  clinicalNotes?: string;
  labName?: string;
  resultDate?: string;
  resultNotes?: string;
  reviewed: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  patient?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    lastLogin: string;
    username: string;
    role: {
      name: string
    }
  };
  orderer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    lastLogin: string;
    username: string;
    role: {
      name: string
    }
  };
}

export interface CreateClinicLabOrderDto {
  patientId: string;
  orderDate?: string;
  priority?: 'routine' | 'urgent';
  tests: Omit<LabTestResult, 'result' | 'unit' | 'referenceRange'>[];
  clinicalNotes?: string;
  labName?: string;
  /** Optional encounter link — set when ordered from an encounter so the
   * order is associated with the visit (parity with imaging/prescriptions). */
  medicalRecordId?: string;
}

export interface UpdateClinicLabOrderDto {
  status?: 'ordered' | 'in-progress' | 'resulted' | 'cancelled';
  priority?: 'routine' | 'urgent';
  tests?: LabTestResult[];
  clinicalNotes?: string;
  labName?: string;
  resultDate?: string;
  resultNotes?: string;
  reviewed?: boolean;
}

export interface FilterClinicLabOrderDto {
  patientId?: string;
  status?: string;
  priority?: string;
  reviewed?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface LabOrderStats {
  pending: number;
  inProgress: number;
  resulted: number;
  unreviewed: number;
}

export const clinicLabOrdersApi = {
  // Get all lab orders with filters
  getAll: async (filters: FilterClinicLabOrderDto = {}): Promise<{
    data: ClinicLabOrder[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const params = new URLSearchParams();
    if (filters.patientId) params.append('patientId', filters.patientId);
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.reviewed !== undefined) params.append('reviewed', String(filters.reviewed));
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await apiClient.get<{
      success: boolean;
      data: {
        data: ClinicLabOrder[];
        total: number;
        page: number;
        limit: number;
      };
    }>(`/clinic-lab-orders?${params.toString()}`);

    return response.data.data;
  },

  // Get lab orders for a specific patient
  getByPatient: async (patientId: string): Promise<ClinicLabOrder[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: ClinicLabOrder[];
    }>(`/clinic-lab-orders/patient/${patientId}`);

    return response.data.data;
  },

  // Get stats
  getStats: async (patientId?: string): Promise<LabOrderStats> => {
    const params = patientId ? `?patientId=${patientId}` : '';
    const response = await apiClient.get<{
      success: boolean;
      data: LabOrderStats;
    }>(`/clinic-lab-orders/stats${params}`);

    return response.data.data;
  },

  // Get single lab order
  getById: async (id: string): Promise<ClinicLabOrder> => {
    const response = await apiClient.get<{
      success: boolean;
      data: ClinicLabOrder;
    }>(`/clinic-lab-orders/${id}`);

    return response.data.data;
  },

  // Session-grouped list: one encounter (visit) per entry. Each group is the
  // array of lab orders raised on that visit. Mirrors prescriptionApi.listGroups.
  listGroups: async (
    filters: FilterClinicLabOrderDto = {},
  ): Promise<{
    data: ClinicLabOrder[][];
    total: number;
    page: number;
    limit: number;
  }> => {
    const params = new URLSearchParams();
    if (filters.patientId) params.append('patientId', filters.patientId);
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.reviewed !== undefined) params.append('reviewed', String(filters.reviewed));
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await apiClient.get<{
      success: boolean;
      data: { data: ClinicLabOrder[][]; total: number; page: number; limit: number };
    }>(`/clinic-lab-orders/groups?${params.toString()}`);

    return response.data.data;
  },

  // All lab orders in one visit's session group.
  getGroup: async (groupId: string): Promise<ClinicLabOrder[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: ClinicLabOrder[];
    }>(`/clinic-lab-orders/groups/${groupId}`);

    return response.data.data ?? [];
  },

  // Create lab order
  create: async (data: CreateClinicLabOrderDto): Promise<ClinicLabOrder> => {
    const response = await apiClient.post<{
      success: boolean;
      data: ClinicLabOrder;
    }>('/clinic-lab-orders', data);

    return response.data.data;
  },

  // Update lab order
  update: async (id: string, data: UpdateClinicLabOrderDto): Promise<ClinicLabOrder> => {
    const response = await apiClient.patch<{
      success: boolean;
      data: ClinicLabOrder;
    }>(`/clinic-lab-orders/${id}`, data);

    return response.data.data;
  },

  // Delete lab order
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/clinic-lab-orders/${id}`);
  },

  // Enter results
  enterResults: async (
    id: string,
    tests: LabTestResult[],
    resultNotes?: string,
  ): Promise<ClinicLabOrder> => {
    const response = await apiClient.post<{
      success: boolean;
      data: ClinicLabOrder;
    }>(`/clinic-lab-orders/${id}/results`, { tests, resultNotes });

    return response.data.data;
  },

  // Mark as reviewed
  markReviewed: async (id: string): Promise<ClinicLabOrder> => {
    const response = await apiClient.post<{
      success: boolean;
      data: ClinicLabOrder;
    }>(`/clinic-lab-orders/${id}/review`);

    return response.data.data;
  },

  // Notify the patient of this lab order via a secure portal deep-link
  // (short message only — no clinical content over the channel).
  notifyPatient: async (
    id: string,
  ): Promise<{ inboxId: string | null; nudgeChannel: string }> => {
    const response = await apiClient.post<{
      success: boolean;
      data: { inboxId: string | null; nudgeChannel: string };
    }>(`/clinic-lab-orders/${id}/notify-patient`);

    return response.data.data;
  },

  // Cancel lab order
  cancel: async (id: string): Promise<ClinicLabOrder> => {
    const response = await apiClient.post<{
      success: boolean;
      data: ClinicLabOrder;
    }>(`/clinic-lab-orders/${id}/cancel`);

    return response.data.data;
  },
};
